import type { ReefIsland } from '../sim/reef';
import { ISLAND_NAMES } from '../sim/reef';
import { escapeHtml } from './format';
import type { World } from '../sim/types';
import { isCleanupHome } from '../sim/operations';
import { shoreDebrisT } from '../sim/shoreDebris';
import { epochDayOf } from '../sim/calendar';

/**
 * The reef's named places (M328, `docs/ONE_REEF_ROADMAP.md`'s O3): real places on Maui Nui's reef, each with its own
 * threat and its own animal, told where the game's events happen there. The simulation keeps its islands; an event at
 * a place acts on its island's numbers, which the reef's average carries.
 *
 * Every fact here is a source's, in the game's own words, each source's quote checked against its text as downloaded
 * (the reader's report, M326; checked by the main session in M328). Their terms are in `docs/SOURCES.md`.
 */

/** A fact about a place and its source, short. */
export interface PlaceFact {
  text: string;
  cite: string;
}

/** A named place on the reef. */
export interface Place {
  id: 'kahekili' | 'honolua' | 'palaau' | 'lanaiShore';
  name: string;
  island: ReefIsland;
  /** What the place is, in a few words. */
  what: string;
  /** Its threat and its animal, as its facts tell them. */
  threat: string;
  animal: string;
  facts: readonly PlaceFact[];
}

export const PLACES: readonly Place[] = [
  {
    // Williams et al. 2016, PLoS ONE 11(7): e0159100 (CC0), p. 3: "The KHFMA, which was established on July 25th 2009,
    // encompasses ~ 3 km of coastline"; p. 7: "Between 2008–9 and 2014–15 parrotfish biomass increased by 139%"; p. 3,
    // the authors' account, compiled: "Sources of excess nutrients in groundwater include the Lahaina Wastewater
    // Treatment Plant, which discharges treated effluent into injection wells".
    id: 'kahekili', name: 'Kahekili', island: 'maui', what: 'Maui\'s herbivore reserve, at Kāʻanapali',
    threat: 'the fishing on its grazing fish, and nutrients from the land', animal: 'parrotfish',
    facts: [
      { text: 'A herbivore reserve since 2009, about 3 km of coast where no parrotfish, surgeonfish or chubs may be taken.', cite: 'Williams et al. 2016' },
      { text: 'Its parrotfish grew 139% by weight in six years of protection, 2008–9 to 2014–15.', cite: 'Williams et al. 2016' },
      { text: 'Nutrients from the Lahaina wastewater plant\'s injection wells reach its reef in the groundwater.', cite: 'Williams et al. 2016' },
    ],
  },
  {
    // Hawaiʻi DAR's page for the Honolua–Mokulēʻia Marine Life Conservation District: "HAR 13-32 45 acres, Established
    // 1978" (State of Hawaiʻi, all rights reserved: its facts only); DAR, "Status and Trends of Maui's Coral Reefs"
    // (data to 2006), p. 1: "declined at 5 reefs, most dramatically at Honolua (42% to 9%)"; Amato et al. 2016, PLoS
    // ONE 11(11): e0165825 (CC0), p. 4: "An intermittent stream at the center of Honolua Bay is a source of sediment".
    id: 'honolua', name: 'Honolua Bay', island: 'maui', what: 'a no-take marine life conservation district, West Maui',
    threat: 'sediment from its stream', animal: 'coral',
    facts: [
      { text: 'A no-take Marine Life Conservation District since 1978, 45 acres.', cite: 'Hawaiʻi DAR' },
      { text: 'Its live coral fell from 42% to 9% between the first surveys and 2006, the steepest fall of Maui\'s monitored reefs.', cite: 'Hawaiʻi DAR' },
      { text: 'A stream at the center of the bay brings sediment onto it in heavy rain.', cite: 'Amato et al. 2016' },
    ],
  },
  {
    // Balazs, Forsyth and Kam 1987, NOAA-TM-NMFS-SWFC-71 (public domain), p. 12: "A large but very shallow fringing reef
    // borders the entire region extending seaward for a distance of 1.0 to 1.5 km"; pp. 13–14: "Bullpen netting at six
    // separate sites at Palaau over 4 days in April and 4 days in July 1985 resulted in 135 captures", "One hundred and
    // thirty-three individual turtles were therefore examined"; p. 17: "Significant foraging habitat for turtles was
    // found all along the Palaau coastline"; p. 14: "The use of gill nets at Palaau commonly occurs"; p. 13: "The
    // sedimentation from coastal soil runoff dominates all areas of the Palaau reef flat."
    id: 'palaau', name: 'Pālāʻau', island: 'molokai', what: 'the reef flat of Molokaʻi\'s south shore',
    threat: 'gill nets, and the soil washed onto it', animal: 'green turtles',
    facts: [
      { text: 'A shallow reef flat reaching 1 to 1.5 km out from shore, soil from the land over all of it.', cite: 'Balazs et al. 1987' },
      { text: 'Green turtles\' foraging pastures: 133 turtles were caught and examined here in 1985.', cite: 'Balazs et al. 1987' },
      { text: 'Gill nets are set here, in what its surveyors thought a very productive area for fish.', cite: 'Balazs et al. 1987' },
    ],
  },
  {
    // PIFSC 2010, SP-10-003 (public domain): the 2008 aerial survey's derelict fishing gear points, Lānaʻi 291 against
    // Maui's 110 and Molokaʻi's 133; "In total, the CRED removed 156 individual DFG piles, totaling 17.4 metric tons"
    // (January 2007, Lānaʻi). Read in M307 (`src/sim/shoreDebris.ts`).
    id: 'lanaiShore', name: 'Lānaʻi\'s shore', island: 'lanai', what: 'the shore where the most derelict gear was counted',
    threat: 'derelict fishing nets', animal: 'the reef the nets smother',
    facts: [
      { text: 'NOAA\'s 2008 aerial survey counted 291 points of derelict fishing gear on Lānaʻi\'s coast, the most of the six islands surveyed.', cite: 'PIFSC 2010' },
      { text: 'In January 2007 NOAA\'s coral reef division removed 156 piles of derelict gear from it, 17.4 tonnes.', cite: 'PIFSC 2010' },
    ],
  },
];

/** The sources' full citations, as the places' folds name them. */
export const PLACE_SOURCES: Readonly<Record<string, string>> = {
  'Williams et al. 2016': 'Williams ID et al. 2016, Responses of herbivorous fishes and benthos to 6 years of protection at the Kahekili Herbivore Fisheries Management Area, Maui. PLoS ONE 11(7): e0159100 (CC0)',
  'Hawaiʻi DAR': 'Hawaiʻi Division of Aquatic Resources: Honolua–Mokulēʻia Marine Life Conservation District (HAR 13-32); Status and Trends of Maui\'s Coral Reefs',
  'Amato et al. 2016': 'Amato DW et al. 2016, Impact of submarine groundwater discharge on marine water quality and reef biota of Maui. PLoS ONE 11(11): e0165825 (CC0)',
  'Balazs et al. 1987': 'Balazs GH, Forsyth RG, Kam AKH 1987, Preliminary assessment of habitat utilization by Hawaiian green turtles in their resident foraging pastures. NOAA-TM-NMFS-SWFC-71',
  'PIFSC 2010': 'NOAA Pacific Islands Fisheries Science Center 2010, SP-10-003',
};

/**
 * The fishing a place's facts tell, for its island's patrol card (M328's review. GAME CHOICE: a patrol is its island's
 * stretch of coast, its catch the island's, so it is never told as a place's; only Pālāʻau's gill nets, a fishing on
 * that coast the source names, stand beside Molokaʻi's patrol as a fact. Kahekili's reserve, where the grazing fish may
 * not be taken at all, is the Protect card's, its result the protection's outlook).
 */
export const fishingAt = (island: ReefIsland): string | null => (island === 'molokai' ? 'Off Pālāʻau, on its south shore\'s reef flat, gill nets are set (Balazs et al. 1987)' : null);

/** The reef's places in one fold: each place, what it is, and its facts with their sources; a shore's own cleanups beside it (M332). */
export function placesHtml(world?: World): string {
  const today = world ? epochDayOf(world) : 0;
  const cleared = (island: ReefIsland): number => (world ? world.operations.finished.filter((f) => isCleanupHome(f) && f.island === island && today - f.endDay < 365).reduce((n, f) => n + (f.tonnes ?? 0), 0) : 0);
  const ours = (p: Place): string => (p.id === 'lanaiShore' && cleared(p.island) > 0 ? ` <span class="row-note">Your cleanups took ${cleared(p.island).toFixed(1)} t off it in the last year; about ${Math.round(shoreDebrisT(world as World, p.island))} t lie there now.</span>` : '');
  const items = PLACES.map((p) => `<li><strong>${escapeHtml(p.name)}</strong>, ${escapeHtml(p.what)} (${escapeHtml(ISLAND_NAMES[p.island])}). <span class="row-note">Its threat: ${escapeHtml(p.threat)}; its animal: ${escapeHtml(p.animal)}.</span>${ours(p)}
    <ul class="rows">${p.facts.map((f) => `<li>${escapeHtml(f.text)} <span class="row-note">${escapeHtml(f.cite)}</span></li>`).join('')}</ul></li>`).join('');
  const sources = [...new Set(PLACES.flatMap((p) => p.facts.map((f) => f.cite)))].map((c) => `<li>${escapeHtml(PLACE_SOURCES[c] ?? c)}</li>`).join('');
  return `<details class="home-more places"><summary>The reef's places</summary><ul class="rows">${items}</ul><details class="advanced"><summary>Sources</summary><ul class="rows">${sources}</ul></details></details>`;
}
